/**
 * Export / import (Deliverable C) — ownership, no lock-in.
 *
 * Three formats, two of them round-trippable:
 *   1. the raw §6.2 event log (`exportLog`, canonical) — Store method;
 *   2. a JSON snapshot of the full application log (`exportJSON`/`importJSON`);
 *   3. a human-readable report (`exportReport`, one-way).
 *
 * The event log is the source of truth: importing it replays to an identical
 * state (round-trip fidelity, tested). The report is derived from state for
 * humans and is intentionally not re-importable.
 */
import { CONTRACT_VERSION } from './version.js';
export function toJSON(events) {
    const snapshot = {
        kind: 'progression-applog',
        contract: CONTRACT_VERSION,
        events,
    };
    return JSON.stringify(snapshot, null, 2);
}
/** The MAJOR component of a semver string (pre-release tags ignored). */
function major(v) {
    return Number.parseInt(v.split('.')[0] ?? '', 10);
}
/**
 * Parse a JSON snapshot back into an app-log, checking the contract tag.
 *
 * Compatibility rule (INV-CONTRACT-STABLE): the event log is forward/backward
 * compatible *within a MAJOR* — events replay deterministically, so a snapshot
 * from any same-major version re-derives safely. A snapshot from a DIFFERENT major
 * may contain events whose shape this build no longer understands, so the import
 * is refused with a clear contract-mismatch error rather than silently producing a
 * wrong state. A missing/foreign tag is also refused.
 */
export function fromJSON(json) {
    const parsed = JSON.parse(json);
    if (parsed === null || typeof parsed !== 'object' || parsed.kind !== 'progression-applog') {
        throw new Error('@progression/app: not a progression-applog JSON snapshot');
    }
    if (typeof parsed.contract !== 'string') {
        throw new Error('@progression/app: snapshot has no contract version tag');
    }
    if (!Array.isArray(parsed.events)) {
        throw new Error('@progression/app: snapshot has no events array');
    }
    const theirs = major(parsed.contract);
    const ours = major(CONTRACT_VERSION);
    if (Number.isNaN(theirs) || theirs !== ours) {
        throw new Error(`@progression/app: contract mismatch — snapshot is v${parsed.contract}, this build serves ` +
            `v${CONTRACT_VERSION} (incompatible MAJOR; import refused so no wrong state is derived).`);
    }
    return parsed.events;
}
/**
 * A human-readable report of the current state: constraints and their status,
 * claims with their (re-derived) verdicts, the three integrity lanes, registered
 * packs, and the history position. `verdictFor` is injected so the report uses the
 * very same kernel-derived verdicts the Store exposes (INV-CORE), not a re-impl.
 */
export function toReport(state, verdictFor, extras) {
    const lines = [];
    lines.push(`# Progression Engine — state report`);
    lines.push(``);
    lines.push(`Contract: ${CONTRACT_VERSION}`);
    lines.push(`History: ${extras.history}`);
    lines.push(``);
    lines.push(`## Constraints (${state.constraints.size})`);
    for (const [id, entry] of state.constraints)
        lines.push(`- ${id} — ${entry.status}`);
    if (state.constraints.size === 0)
        lines.push(`- (none)`);
    lines.push(``);
    lines.push(`## Claims (${state.claims.size})`);
    for (const [id, claim] of state.claims) {
        const v = verdictFor(id);
        const conflict = v.conflictingConstraints.length
            ? ` — conflicts: ${v.conflictingConstraints.join(', ')}`
            : '';
        lines.push(`- ${id} — ${v.state} [W${claim.profile.w} ${claim.profile.e} M${claim.profile.m}]${conflict}`);
    }
    if (state.claims.size === 0)
        lines.push(`- (none)`);
    lines.push(``);
    lines.push(`## Integrity lanes (${state.integrity.size})`);
    for (const sc of state.integrity.values()) {
        lines.push(`- ${sc.id} [${sc.layer}] — machine:${sc.lanes.machine} expert:${sc.lanes.expert} community:${sc.lanes.community}`);
    }
    lines.push(``);
    lines.push(`## Domain packs (${extras.packs.length})`);
    for (const p of extras.packs)
        lines.push(`- ${p}`);
    if (extras.packs.length === 0)
        lines.push(`- (none)`);
    lines.push(``);
    return lines.join('\n');
}
//# sourceMappingURL=exporters.js.map